import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from "@angular/router";

import { JwtModule } from '@auth0/angular-jwt';
import { DocumentRef } from '@dav-core/reference/document.reference';
import { WindowRef } from '@dav-core/reference/window.reference';
import { TranslationLoader } from '@dav-core/translate-loader/translate.loader';
import { CartEffects } from '@dav-store/cart/cart.effects';
import { ClaimsEffects } from '@dav-store/claims/claims.effects';
import { ConfigEffects } from '@dav-store/config/config.effects';
import { HistoryEffects } from '@dav-store/history/history.effects';
import { PrismicEffects } from '@dav-store/prismic/prismic.effects';
import { ProductEffects } from '@dav-store/product/product.effects';
import { reducers } from '@dav-store/reducers';
import { RemitEffects } from '@dav-store/remit/remit.effects';
import { UserEffects } from '@dav-store/user/user.effects';
import { VehicleEffects } from '@dav-store/vehicle/vehicle.effects';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { localStorageSync } from 'ngrx-store-localstorage';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';

import { APP_ROUTES } from './app/app-routes';
import { AppComponent } from './app/app.component';
import { requestInterceptor } from './app/interceptor/interceptor';
import { environment } from './environments/environment';

import 'hammerjs';
import { ApiService } from "@dav-core/services/api.service";

const localStorageSyncReducer = (reducer: ActionReducer<any>): ActionReducer<any> => {
  return localStorageSync({ keys: ['userState', 'productState', 'cartState', 'vehicleState'], rehydrate: true })(
    reducer,
  );
}

const metaReducers: MetaReducer<any, any>[] = [localStorageSyncReducer];

// required for AOT compilation
const HttpLoaderFactory = (http: HttpClient, apiService: ApiService) => new TranslationLoader(apiService);

const tokenGetter = () => sessionStorage.getItem('token');

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      ToastrModule.forRoot(),
      MatNativeDateModule,
      // Add this import here
      JwtModule.forRoot({
        config: { tokenGetter: tokenGetter }
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient, ApiService],
        },
      }),
      NgbModule,
      StoreModule.forRoot(reducers, {
        metaReducers, runtimeChecks: {
          strictActionImmutability: false,
          strictActionSerializability: false,
          strictActionTypeUniqueness: false,
          strictActionWithinNgZone: false,
          strictStateImmutability: false,
          strictStateSerializability: false,
        },
      }),
      StoreDevtoolsModule.instrument({ maxAge: 10 }),
      EffectsModule.forRoot([
        ProductEffects,
        ConfigEffects,
        CartEffects,
        UserEffects,
        VehicleEffects,
        RemitEffects,
        HistoryEffects,
        ClaimsEffects,
        PrismicEffects,
      ]),
      MatMomentDateModule),
    CookieService,
    requestInterceptor,
    WindowRef,
    DocumentRef,
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation): string => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ]
});
